import React, { useCallback, useContext, useEffect, useState } from 'react';
import { axios, baseUrl } from 'Api';
import { AppContext, AuthContext } from 'Context';
import { ProjectorShimmer } from 'Components';
import AlterImage from 'Assets/alter-image.png';
import { ViewerPage } from '../components/page/PageClassic';
import styles from '../viewers/ViewersClassic.module.css';

// Viewers → Connected Accounts. Laid out like the Viewers list (same header
// row, rows and Remove button); the data is the affiliate's connected accounts
// and removal goes through removeInviteManager.
export const ConnectedAccountsClassic = () => {
  const { getToken } = useContext(AuthContext);
  const { isTokenChange, setAppSnackbar, langText } = useContext(AppContext);
  const commonText = langText?.common;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await axios.post(`${baseUrl}/inviteUsersList`, { token }).catch(() => null);
      const data = res?.data;
      const list = Array.isArray(data) ? data : (data?.data || data?.users || []);
      setUsers(Array.isArray(list) ? list : []);
    } catch (e) {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { loadUsers(); }, [loadUsers, isTokenChange]);

  // Same call the old user-management list used (UserList.jsx → removeInviteManager).
  const handleRemove = async (u) => {
    if (!u?.id) return;
    setRemovingId(u.id);
    try {
      const token = await getToken();
      const { data } = await axios.post(`${baseUrl}/removeInviteManager`, { token, id: u.id });
      if (data?.success === false) {
        setAppSnackbar?.({ isVisible: true, type: 'error', message: data?.message || 'Oops! Something went wrong' });
        return;
      }
      setUsers((prev) => prev.filter((row) => row.id !== u.id));
      setAppSnackbar?.({
        isVisible: true,
        message: `${u?.firstname ? `${u.firstname}` : 'User'} removed`,
      });
    } catch (error) {
      setAppSnackbar?.({
        isVisible: true,
        type: 'error',
        message: error?.response?.data?.message || 'Oops! Something went wrong',
      });
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <ViewerPage title="Connected Accounts">
      {/* The Viewers page gets this gap from its filter row; this page has none. */}
      <div className={styles.viewersDiv} style={{ marginTop: '24px' }}>
        <div className={styles.allViewersContainer}>
          {loading ? (
            <ProjectorShimmer isLight />
          ) : (
            <div className={styles.viewerList}>
              <div className={styles.memberHeaderRow}>
                <span className={styles.memberHeaderName}>Name</span>
                <span className={styles.memberHeaderEmail}>Email</span>
                <span className={styles.memberHeaderActions}>Actions</span>
              </div>

              {!users.length ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '320px',
                  padding: '16px',
                  textAlign: 'center',
                  color: '#6b7280',
                  fontSize: '16px',
                  fontWeight: 500,
                }}
                >
                  No connected accounts yet
                </div>
              ) : users.map((u, idx) => {
                const firstname = u?.firstname || '';
                const lastname = u?.lastname || '';
                const email = u?.email || '';
                const image = u?.dpURL || u?.image;
                return (
                  <div className={styles.userRequest} key={u?.id || email || idx}>
                    <div className={styles.userMemberStyle}>
                      {image ? (
                        <img
                          className={styles.profileIcon}
                          src={image || AlterImage}
                          alt={firstname || email}
                          onError={(e) => { e.target.src = AlterImage; }}
                        />
                      ) : (
                        <div className={styles.profileIcon}>
                          {(firstname || email).charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className={styles.userRequestText}>
                        <div className={styles.userRequestName}>{`${firstname} ${lastname}`.trim() || '—'}</div>
                      </div>
                    </div>
                    <div className={styles.userRequestEmail}>{email}</div>
                    <div className={styles.userRequestActionsContainer}>
                      <button
                        className={styles.userRequestActionButton}
                        disabled={removingId === u?.id}
                        onClick={() => handleRemove(u)}
                      >
                        {removingId === u?.id
                          ? `${(commonText?.removing || 'Removing')}...`
                          : (commonText?.remove || 'Remove')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </ViewerPage>
  );
};
